import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSkin } from '@/lib/skin';
import { openQuickLog } from '@/features/track/logEvents';

// Five equal tabs, Lucide-style line icons (DESIGN.md › Navigation). Paths match the routes in router.tsx.
const TABS = [
  { to: '/', key: 'plan', end: true, icon: <><path d="M8 2v4" /><path d="M16 2v4" /><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18" /></> },
  { to: '/track', key: 'track', icon: <><path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="m19 9-5 5-4-4-3 3" /></> },
  { to: '/sounds', key: 'sounds', icon: <><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" /><path d="M15.5 10v4" /><path d="M18.5 7.5v9" /><path d="M21.5 10.5v3" /></> },
  { to: '/learn', key: 'learn', icon: <><g transform="translate(2.4 3.4) scale(.8)" strokeWidth="2.5"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" /><path d="M9 18h6" /><path d="M10 22h4" /></g><path d="M12 1v1.6" /><path d="m18.4 3.6-1.2 1.2" /><path d="m5.6 3.6 1.2 1.2" /><path d="M21.6 9.8H20" /><path d="M2.4 9.8H4" /></> },
  { to: '/chat', key: 'chat', icon: <><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" /><path d="M8 12h.01" /><path d="M12 12h.01" /><path d="M16 12h.01" /></> },
];

// Glass icons: soft line icons. Sounds is not in the Glass menu: the home button already plays and picks every sound.
const GLASS_ICONS: Record<string, React.ReactNode> = {
  plan: <path d="M12 20.5s-8-4.6-8-10.4A4.4 4.4 0 0 1 12 7.6a4.4 4.4 0 0 1 8 2.5c0 5.8-8 10.4-8 10.4Z" />,
  learn: <><path d="M4 5.5C6.5 4.3 9.3 4.4 12 6c2.7-1.6 5.5-1.7 8-.5v13c-2.5-1.2-5.3-1.1-8 .5-2.7-1.6-5.5-1.7-8-.5v-13Z" /><path d="M12 6v13" /></>,
};

export function TabBar() {
  const { t } = useTranslation('common');
  const glass = useSkin() === 'glass';
  const tabs = glass ? TABS.filter((x) => x.key !== 'sounds') : TABS;
  return (
    <nav className="tabbar" aria-label={t('nav.label')}>
      <ul>
        {tabs.flatMap((tab, i) => {
          const item = (
            <li key={tab.key}>
              <NavLink to={tab.to} end={tab.end} data-tour={`tab-${tab.key}`}>
                <svg viewBox="0 0 24 24" aria-hidden="true">{(glass && GLASS_ICONS[tab.key]) || tab.icon}</svg>
                {t(`nav.${glass && tab.key === 'plan' ? 'today' : tab.key}`)}
              </NavLink>
            </li>
          );
          // Glass: a neutral "+" in the middle opens the quick-log quiz from any tab
          if (glass && i === 2) return [
            <li key="log"><button className="tab-fab" data-tour="log" type="button" aria-label={t('nav.log')} onClick={() => openQuickLog('start', 'fab')}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            </button></li>, item];
          return [item];
        })}
      </ul>
      <div className="home-indicator" aria-hidden="true" />
    </nav>
  );
}
